import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

/** ชนิดข้อมูลฝั่ง admin — ตรงกับ DTO ที่ API ส่งกลับ */
export interface Blog {
  id: string; tenantId: string; slug: string; urlMode: 'PATH' | 'CUSTOM';
  title: string; description: string | null;
  logoUrl: string | null; faviconUrl: string | null; coverUrl: string | null;
  themeCode: string; themeConfig: Record<string, unknown>;
  defaultLocale: string; locales: string[]; postsPerPage: number;
  metaTitle: string | null; metaDescription: string | null; ogImageUrl: string | null;
  googleAnalyticsId: string | null; gtmId: string | null;
  customDomain: string | null; customDomainStatus: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  url: string; createdAt: string; updatedAt: string;
  stats?: { published: number; draft: number; total: number };
}

export interface Category {
  id: string; blogId: string; parentId: string | null; slug: string; name: string;
  description: string | null; color: string | null; icon: string | null;
  sortOrder: number; postCount: number;
}

export interface Tag { id: string; blogId: string; slug: string; name: string; usageCount: number }

export interface PostListItem {
  id: string; slug: string; locale: string; title: string; excerpt: string | null;
  coverImageUrl: string | null;
  category: { id: string; name: string; slug: string; color: string | null } | null;
  author: { id: string; name: string; avatarUrl: string | null };
  status: 'DRAFT' | 'IN_REVIEW' | 'SCHEDULED' | 'PUBLISHED' | 'ARCHIVED';
  isFeatured: boolean; isPinned: boolean;
  publishedAt: string | null; scheduledAt: string | null;
  readingMinutes: number | null; viewCount: number; widgetClickCount: number;
  url: string; updatedAt: string;
}

export interface Post extends PostListItem {
  blogId: string; contentJson: unknown | null; contentHtml: string | null;
  coverImageId: string | null;
  metaTitle: string | null; metaDescription: string | null; ogImageUrl: string | null;
  canonicalUrl: string | null; noindex: boolean;
  tags: Tag[]; revisionNo: number; createdAt: string;
}

export interface Widget {
  id: string; tenantId: string; blogId: string; publicKey: string; name: string;
  templateCode: string; config: Record<string, any>;
  filterCategoryIds: string[]; filterTagIds: string[];
  filterLocale: string | null; filterFeaturedOnly: boolean;
  sortBy: string; isDomainLocked: boolean; cacheTtlSec: number;
  status: 'ACTIVE' | 'PAUSED' | 'DELETED';
  impressionCount: number; clickCount: number; lastSeenAt: string | null;
  domains: string[]; embedCode: string; createdAt: string; updatedAt: string;
}

export interface Media {
  id: string; originalName: string; mimeType: string; sizeBytes: number;
  width: number | null; height: number | null; blurhash: string | null;
  altText: string | null; publicUrl: string; variants: Record<string, string> | null;
  status: string; createdAt: string;
}

export interface Quota {
  blogs: { used: number; max: number };
  posts: { used: number; max: number };
  widgets: { used: number; max: number };
  members: { used: number; max: number };
  storageMb: { used: number; max: number };
  allowCustomDomain: boolean; showBranding: boolean;
}

export interface TemplateInfo {
  code: string; kind: 'WIDGET' | 'BLOG_THEME'; name: string; description: string | null;
  schemaJson: any; defaultConfig: any;
  minPlan: string; available: boolean; comingSoon: boolean; sortOrder: number;
}

export interface Paged<T> {
  data: T[];
  meta: { page: number; perPage: number; total: number; totalPages: number };
}

// ------------------------------ hooks ------------------------------

export const useBlogs = () =>
  useQuery({ queryKey: ['blogs'], queryFn: () => api.get<{ data: Blog[] }>('/v1/blogs').then((r) => r.data) });

export const useBlog = (id: string | undefined) =>
  useQuery({
    queryKey: ['blog', id],
    queryFn: () => api.get<{ data: Blog }>(`/v1/blogs/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  });

export const useCategories = (blogId: string | undefined) =>
  useQuery({
    queryKey: ['categories', blogId],
    queryFn: () => api.get<{ data: Category[] }>(`/v1/blogs/${blogId}/categories`).then((r) => r.data),
    enabled: Boolean(blogId),
  });

export const useTags = (blogId: string | undefined) =>
  useQuery({
    queryKey: ['tags', blogId],
    queryFn: () => api.get<{ data: Tag[] }>(`/v1/blogs/${blogId}/tags`).then((r) => r.data),
    enabled: Boolean(blogId),
  });

export interface AuditLogEntry {
  id: number;
  action: string;
  entityType: string | null;
  entityId: string | null;
  changes: { before?: Record<string, unknown>; after?: Record<string, unknown> } | null;
  user: { id: string; fullName: string; email: string } | null;
  createdAt: string;
}

export const useAuditLogs = (query: Record<string, string | number | undefined>) =>
  useQuery({
    queryKey: ['audit-logs', query],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') params.set(k, String(v));
      return api.get<Paged<AuditLogEntry>>(`/v1/audit-logs?${params}`);
    },
    placeholderData: (prev) => prev,   // ไม่ให้ตารางกระพริบตอนเปลี่ยนหน้า/ตัวกรอง
  });

export const usePosts = (blogId: string | undefined, query: Record<string, string | number | undefined>) =>
  useQuery({
    queryKey: ['posts', blogId, query],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') params.set(k, String(v));
      return api.get<Paged<PostListItem>>(`/v1/blogs/${blogId}/posts?${params}`);
    },
    enabled: Boolean(blogId),
    placeholderData: (prev) => prev,   // ไม่ให้ตารางกระพริบตอนเปลี่ยนหน้า/ตัวกรอง
  });

export const usePost = (id: string | undefined) =>
  useQuery({
    queryKey: ['post', id],
    queryFn: () => api.get<{ data: Post }>(`/v1/posts/${id}`).then((r) => r.data),
    enabled: Boolean(id) && id !== 'new',
  });

export const useWidgets = () =>
  useQuery({ queryKey: ['widgets'], queryFn: () => api.get<{ data: Widget[] }>('/v1/widgets').then((r) => r.data) });

export const useWidget = (id: string | undefined) =>
  useQuery({
    queryKey: ['widget', id],
    queryFn: () => api.get<{ data: Widget }>(`/v1/widgets/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  });

export const useQuota = () =>
  useQuery({ queryKey: ['quota'], queryFn: () => api.get<{ data: Quota }>('/v1/tenants/usage').then((r) => r.data) });

export const useTemplates = (kind?: 'WIDGET' | 'BLOG_THEME') =>
  useQuery({
    queryKey: ['templates', kind],
    queryFn: () => api.get<{ data: TemplateInfo[] }>(`/v1/templates${kind ? `?kind=${kind}` : ''}`).then((r) => r.data),
    staleTime: 5 * 60_000,   // catalog แทบไม่เปลี่ยน — ไม่ต้องยิงซ้ำทุกครั้งที่เปลี่ยนหน้า
  });

export const useMedia = (query: Record<string, string | number | undefined> = {}) =>
  useQuery({
    queryKey: ['media', query],
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') params.set(k, String(v));
      return api.get<Paged<Media>>(`/v1/media?${params}`);
    },
    placeholderData: (prev) => prev,
  });

/** invalidate หลาย key พร้อมกัน — ใช้หลัง mutation ที่กระทบหลายหน้า */
export function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: unknown[][]) => {
    for (const key of keys) void qc.invalidateQueries({ queryKey: key });
  };
}

export { useQuery, useMutation, useQueryClient };
